/**
 * PartnerForm — modal form to create an enterprise SSO partner.
 *
 * Fields:
 *   - name (text, 1-100 chars)
 *   - slug (text, regex ^[a-z0-9-]{1,64}$ — must match M001 CHECK)
 *   - provisioning_mode (radio: 'shared' | 'per_user')
 *   - default_member_role (select: 'viewer' | 'member' | 'admin') — only for shared
 *   - portal_url (optional URL)
 *   - workspace_owner_user_id (search-select against public.users)
 *   - initial_admin_user_ids (multi-select, MUST include workspace_owner_user_id
 *     per M005 partner_create's owner_must_be_admin P0020 check).
 *
 * On success, calls onSuccess(signing_secret_plaintext) so the parent can show
 * the one-time secret reveal modal (PartnerSecretRotation).
 */
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useQuery } from '@tanstack/react-query';
import { Loader2, X } from 'lucide-react';

import { supabase } from '@/lib/supabase';
import { useToast } from '@/hooks/use-toast';
import { useCreatePartner } from '@/hooks/useEnterpriseSsoActions';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { captureError } from '@/lib/sentry';

// ---------------------------------------------------------------
// Schema — slug regex MUST match M001 CHECK constraint exactly:
// CHECK (slug ~ '^[a-z0-9-]{1,64}$')
// ---------------------------------------------------------------
const slugRegex = /^[a-z0-9-]{1,64}$/;

const partnerFormSchema = z.object({
  name: z
    .string()
    .min(1, 'Name is required')
    .max(100, 'Name must be 100 characters or fewer'),
  slug: z
    .string()
    .min(1, 'Slug is required')
    .max(64, 'Slug must be 64 characters or fewer')
    .regex(
      slugRegex,
      'Slug must contain only lowercase letters, numbers, and hyphens (1-64 chars)',
    ),
  provisioning_mode: z.enum(['shared', 'per_user'], {
    required_error: 'Provisioning mode is required',
  }),
  default_member_role: z.enum(['viewer', 'member', 'admin']).optional(),
  portal_url: z
    .string()
    .url('Must be a valid URL (https://…)')
    .optional()
    .or(z.literal('')),
  workspace_owner_user_id: z
    .string()
    .uuid('Workspace owner is required'),
  initial_admin_user_ids: z
    .array(z.string().uuid())
    .min(1, 'At least one initial admin is required'),
});

type PartnerFormValues = z.infer<typeof partnerFormSchema>;

interface UserSearchRow {
  id: string;
  email: string | null;
  first_name: string | null;
  last_name: string | null;
}

function userDisplay(u: UserSearchRow | undefined): string {
  if (!u) return '';
  const name = [u.first_name, u.last_name].filter(Boolean).join(' ').trim();
  if (name && u.email) return `${name} (${u.email})`;
  if (name) return name;
  if (u.email) return u.email;
  return u.id;
}

// ---------------------------------------------------------------
// Debounced user search hook (queries public.users)
// ---------------------------------------------------------------
function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState<T>(value);
  useEffect(() => {
    const handle = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(handle);
  }, [value, delayMs]);
  return debounced;
}

function useUserSearch(searchTerm: string) {
  const debounced = useDebouncedValue(searchTerm.trim(), 250);
  return useQuery<UserSearchRow[]>({
    queryKey: ['enterprise-sso', 'user-search', debounced],
    queryFn: async () => {
      // Calls the M009 SECURITY DEFINER RPC, which joins public.users to
      // auth.users and gates by super_admin. Direct query against
      // public.users would fail — that table has no email column.
      if (debounced.length === 0) return [];
      const { data, error } = await supabase.rpc('enterprise_sso_user_search', {
        p_q: debounced,
        p_limit: 20,
      });
      if (error) throw error;
      return (data ?? []) as UserSearchRow[];
    },
    enabled: debounced.length >= 2,
    staleTime: 30_000,
  });
}

// ---------------------------------------------------------------
// User-by-id lookup (for displaying selected users by name/email
// without re-running search). Uses the same M009 RPC with the
// (truncated) UUID as a search term — staff-callable + returns email.
// ---------------------------------------------------------------
function useUsersByIds(ids: string[]) {
  return useQuery<UserSearchRow[]>({
    queryKey: ['enterprise-sso', 'users-by-ids', [...ids].sort().join(',')],
    queryFn: async () => {
      if (ids.length === 0) return [];
      const { data, error } = await supabase.rpc('enterprise_sso_users_by_ids', {
        p_ids: ids,
      });
      if (error) throw error;
      return (data ?? []) as UserSearchRow[];
    },
    enabled: ids.length > 0,
    staleTime: 60_000,
  });
}

// ---------------------------------------------------------------
// User search dropdown (single-select)
// ---------------------------------------------------------------
interface UserSearchSelectProps {
  value: string;
  onChange: (userId: string) => void;
  disabled?: boolean;
  placeholder?: string;
  /** Optional ids whose details we want to render even when not in the
   * current search results (e.g., the currently-selected value). */
  preloadIds?: string[];
}

function UserSearchSelect({
  value,
  onChange,
  disabled,
  placeholder = 'Search by name or email…',
  preloadIds = [],
}: UserSearchSelectProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [open, setOpen] = useState(false);
  const { data: results = [], isFetching } = useUserSearch(searchTerm);
  const idsToPreload = useMemo(
    () => Array.from(new Set([value, ...preloadIds].filter(Boolean))),
    [value, preloadIds],
  );
  const { data: preloaded = [] } = useUsersByIds(idsToPreload);

  const selectedUser = useMemo(
    () =>
      preloaded.find((u) => u.id === value) ??
      results.find((u) => u.id === value),
    [value, preloaded, results],
  );

  return (
    <div className="relative">
      <Input
        value={open ? searchTerm : userDisplay(selectedUser)}
        onChange={(e) => {
          setSearchTerm(e.target.value);
          if (!open) setOpen(true);
        }}
        onFocus={() => {
          setOpen(true);
          setSearchTerm('');
        }}
        onBlur={() => {
          // Delay so click on result fires first
          setTimeout(() => setOpen(false), 150);
        }}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
      />
      {open && (
        <div className="absolute z-50 mt-1 w-full overflow-hidden rounded-md border bg-popover shadow-md">
          <div className="max-h-[240px] overflow-y-auto">
            {isFetching && (
              <div className="px-3 py-2 text-sm text-muted-foreground">
                Searching…
              </div>
            )}
            {!isFetching && results.length === 0 && (
              <div className="px-3 py-2 text-sm text-muted-foreground">
                No users found
              </div>
            )}
            {results.map((u) => (
              <button
                key={u.id}
                type="button"
                className="block w-full cursor-pointer px-3 py-2 text-left text-sm hover:bg-accent"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  onChange(u.id);
                  setSearchTerm('');
                  setOpen(false);
                }}
              >
                {userDisplay(u)}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------
// Multi-user search (chips + dropdown)
// ---------------------------------------------------------------
interface UserMultiSelectProps {
  value: string[];
  onChange: (userIds: string[]) => void;
  /** Ids that cannot be removed (e.g., the workspace owner). */
  lockedIds?: string[];
  disabled?: boolean;
  placeholder?: string;
}

function UserMultiSelect({
  value,
  onChange,
  lockedIds = [],
  disabled,
  placeholder = 'Add admins by name or email…',
}: UserMultiSelectProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [open, setOpen] = useState(false);
  const { data: results = [], isFetching } = useUserSearch(searchTerm);
  const { data: selectedUsers = [] } = useUsersByIds(value);

  const lockedSet = useMemo(() => new Set(lockedIds), [lockedIds]);
  const selectedSet = useMemo(() => new Set(value), [value]);

  const filteredResults = useMemo(
    () => results.filter((u) => !selectedSet.has(u.id)),
    [results, selectedSet],
  );

  const addUser = (id: string) => {
    if (selectedSet.has(id)) return;
    onChange([...value, id]);
    setSearchTerm('');
  };

  const removeUser = (id: string) => {
    if (lockedSet.has(id)) return;
    onChange(value.filter((v) => v !== id));
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1.5 rounded-md border bg-background p-2">
        {selectedUsers.length === 0 && (
          <span className="px-1 text-sm text-muted-foreground">
            No admins selected
          </span>
        )}
        {selectedUsers.map((u) => {
          const locked = lockedSet.has(u.id);
          return (
            <span
              key={u.id}
              className="inline-flex items-center gap-1 rounded-full bg-secondary px-2.5 py-0.5 text-xs"
            >
              {userDisplay(u)}
              {locked ? (
                <span
                  className="text-[10px] uppercase tracking-wide text-muted-foreground"
                  title="Workspace owner — must be an admin"
                >
                  owner
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() => removeUser(u.id)}
                  className="rounded-full p-0.5 hover:bg-muted-foreground/10"
                  disabled={disabled}
                  aria-label={`Remove ${userDisplay(u)}`}
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </span>
          );
        })}
      </div>
      <div className="relative">
        <Input
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            if (!open) setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => {
            setTimeout(() => setOpen(false), 150);
          }}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="off"
        />
        {open && (
          <div className="absolute z-50 mt-1 w-full overflow-hidden rounded-md border bg-popover shadow-md">
            <div className="max-h-[240px] overflow-y-auto">
              {isFetching && (
                <div className="px-3 py-2 text-sm text-muted-foreground">
                  Searching…
                </div>
              )}
              {!isFetching && filteredResults.length === 0 && (
                <div className="px-3 py-2 text-sm text-muted-foreground">
                  No users found
                </div>
              )}
              {filteredResults.map((u) => (
                <button
                  key={u.id}
                  type="button"
                  className="block w-full cursor-pointer px-3 py-2 text-left text-sm hover:bg-accent"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => addUser(u.id)}
                >
                  {userDisplay(u)}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------
// PartnerForm component
// ---------------------------------------------------------------
export interface PartnerFormProps {
  mode: 'create';
  onSuccess: (signingSecretPlaintext: string) => void;
  onClose: () => void;
}

export function PartnerForm({
  mode,
  onSuccess,
  onClose,
}: PartnerFormProps): JSX.Element {
  const { toast } = useToast();
  const createPartner = useCreatePartner();

  const form = useForm<PartnerFormValues>({
    resolver: zodResolver(partnerFormSchema),
    defaultValues: {
      name: '',
      slug: '',
      provisioning_mode: 'shared',
      default_member_role: 'member',
      portal_url: '',
      workspace_owner_user_id: '',
      initial_admin_user_ids: [],
    },
  });

  const provisioningMode = form.watch('provisioning_mode');
  const ownerUserId = form.watch('workspace_owner_user_id');
  const adminUserIds = form.watch('initial_admin_user_ids');

  // Auto-add the workspace_owner to initial_admin_user_ids whenever it changes.
  // This satisfies M005 partner_create's owner_must_be_admin (P0020) check.
  useEffect(() => {
    if (!ownerUserId) return;
    if (!adminUserIds.includes(ownerUserId)) {
      form.setValue(
        'initial_admin_user_ids',
        [...adminUserIds, ownerUserId],
        { shouldDirty: true, shouldValidate: true },
      );
    }
    // We intentionally exclude adminUserIds from deps to avoid loops; we only
    // want to react when the owner id changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownerUserId]);

  const isSubmitting = createPartner.isPending;

  const onSubmit = async (values: PartnerFormValues) => {
    try {
      // Defensive: if owner somehow not in admins, add it client-side
      // (the RPC will reject otherwise with owner_must_be_admin).
      const adminIds = values.initial_admin_user_ids.includes(
        values.workspace_owner_user_id,
      )
        ? values.initial_admin_user_ids
        : [...values.initial_admin_user_ids, values.workspace_owner_user_id];

      const result = await createPartner.mutateAsync({
        name: values.name,
        slug: values.slug,
        provisioning_mode: values.provisioning_mode,
        // Only send default_member_role for shared mode. M005's per_user finalize
        // hardcodes 'admin' regardless.
        default_member_role:
          values.provisioning_mode === 'shared'
            ? values.default_member_role
            : undefined,
        portal_url:
          values.portal_url && values.portal_url.length > 0
            ? values.portal_url
            : undefined,
        workspace_owner_user_id: values.workspace_owner_user_id,
        initial_admin_user_ids: adminIds,
      });

      onSuccess(result.signing_secret_plaintext);
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : 'Unknown error creating partner';
      captureError(err as Error, { feature: 'enterprise-partner-form-on-submit' });
      toast({
        title: 'Could not create partner',
        description: message,
        variant: 'destructive',
      });
      // Stay open so the user can retry.
    }
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !isSubmitting) onClose();
      }}
    >
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col p-0 gap-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b">
          <DialogTitle>
            {mode === 'create'
              ? 'Create enterprise partner'
              : 'Edit enterprise partner'}
          </DialogTitle>
          <DialogDescription>
            Configure a new enterprise SSO partner. The signing secret will be
            shown once after creation — copy it to a secure location before
            closing the dialog.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex flex-col flex-1 overflow-hidden"
            noValidate
          >
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
            {/* Name */}
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Partner name</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="Acme Corp"
                      disabled={isSubmitting}
                      autoComplete="off"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Slug */}
            <FormField
              control={form.control}
              name="slug"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Slug</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="acme-corp"
                      disabled={isSubmitting}
                      autoComplete="off"
                    />
                  </FormControl>
                  <FormDescription>
                    Lowercase letters, numbers, and hyphens only. 1–64 characters.
                    Used in SSO URLs and cannot be changed later.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Provisioning mode */}
            <FormField
              control={form.control}
              name="provisioning_mode"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Provisioning mode</FormLabel>
                  <FormControl>
                    <RadioGroup
                      onValueChange={field.onChange}
                      value={field.value}
                      disabled={isSubmitting}
                      className="grid gap-3"
                    >
                      <div className="flex items-start gap-3 rounded-md border p-3">
                        <RadioGroupItem value="shared" id="mode-shared" />
                        <div className="grid gap-1">
                          <Label
                            htmlFor="mode-shared"
                            className="font-medium"
                          >
                            Shared workspace
                          </Label>
                          <p className="text-xs text-muted-foreground">
                            All employees join one workspace. Use for teams who
                            collaborate on shared contacts.
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3 rounded-md border p-3">
                        <RadioGroupItem value="per_user" id="mode-per-user" />
                        <div className="grid gap-1">
                          <Label
                            htmlFor="mode-per-user"
                            className="font-medium"
                          >
                            Per-user workspace
                          </Label>
                          <p className="text-xs text-muted-foreground">
                            Each employee gets their own workspace. Use for
                            independent agents or financial advisors.
                          </p>
                        </div>
                      </div>
                    </RadioGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Default member role — only for shared mode */}
            {provisioningMode === 'shared' && (
              <FormField
                control={form.control}
                name="default_member_role"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Default member role</FormLabel>
                    <FormControl>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value ?? 'member'}
                        disabled={isSubmitting}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select a default role" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="viewer">Viewer</SelectItem>
                          <SelectItem value="member">Member</SelectItem>
                          <SelectItem value="admin">Admin</SelectItem>
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormDescription>
                      Role assigned to employees provisioned into the shared
                      workspace.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {/* Portal URL */}
            <FormField
              control={form.control}
              name="portal_url"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Portal URL (optional)</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="https://portal.acme.com"
                      disabled={isSubmitting}
                      autoComplete="off"
                    />
                  </FormControl>
                  <FormDescription>
                    Where employees return after signing out.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Workspace owner */}
            <FormField
              control={form.control}
              name="workspace_owner_user_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Workspace owner</FormLabel>
                  <FormControl>
                    <UserSearchSelect
                      value={field.value}
                      onChange={field.onChange}
                      disabled={isSubmitting}
                    />
                  </FormControl>
                  <FormDescription>
                    The BuildIt user who owns workspaces provisioned for this
                    partner. Automatically included in initial admins.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Initial admins */}
            <FormField
              control={form.control}
              name="initial_admin_user_ids"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Initial admins</FormLabel>
                  <FormControl>
                    <UserMultiSelect
                      value={field.value}
                      onChange={field.onChange}
                      lockedIds={ownerUserId ? [ownerUserId] : []}
                      disabled={isSubmitting}
                    />
                  </FormControl>
                  <FormDescription>
                    BuildIt users authorized to manage this partner. The
                    workspace owner is required and cannot be removed.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            </div>

            <DialogFooter className="px-6 py-4 border-t bg-background">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating…
                  </>
                ) : (
                  'Create partner'
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
